import React, { useState } from 'react';
import { BARK_FLAVORS } from '../mock';
import { MessageCircle, Check, Sparkles, Leaf } from 'lucide-react';

// Barks hasn't launched yet — there's nothing to add to cart, so "notify
// me" goes to WhatsApp instead of collecting an email nobody's set up a
// mailing flow for yet.
const WHATSAPP_NUMBER = '919167798999';
const waLink = (flavorName) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hi Raretto! I'd love to be notified when Barks launches — especially the "${flavorName}" flavour. Could you keep me posted?`
  )}`;

const BarksComingSoon = () => {
  const [selectedFlavor, setSelectedFlavor] = useState(BARK_FLAVORS[0].id);

  return (
    <main>
      {/* HERO */}
      <section className="relative bg-[#1a120b] text-[#f6efe1] overflow-hidden">
        <div className="container-lux relative z-10 py-24 md:py-32 grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 border border-[#b78d3c]/40 rounded-full px-4 py-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#b78d3c]" />
              <span className="tracking-luxe text-[#b78d3c] text-[10px]">Coming Soon</span>
            </div>
            <h1 className="font-display text-6xl md:text-8xl leading-[1]">
              Raretto <em className="font-serif-alt not-italic text-[#b78d3c]">Barks</em>
            </h1>
            <p className="text-lg text-[#f6efe1]/75 leading-relaxed max-w-lg">
              Hand-broken slabs of premium chocolate, layered with fruit, nut and botanical inclusions. Six flavours, starting with a cooling, unmistakably-Indian signature.
            </p>
            <div className="flex items-center gap-3">
              <span className="tracking-luxe text-[#b78d3c]">Launching</span>
              <span className="font-display text-3xl">Winter ’25</span>
            </div>
          </div>

          {/* Signature card — Mango Chill */}
          <div className="relative aspect-[4/5] max-w-md mx-auto w-full overflow-hidden">
            <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, #e59a2b 0%, #b34b1a 60%, #1a120b 100%)` }}></div>
            <div className="absolute inset-0 grain"></div>
            <div className="absolute top-6 left-6 right-6 flex justify-between items-start">
              <p className="tracking-luxe text-[#f6efe1]/90">The Signature</p>
              <span className="bg-[#f6efe1] text-[#1a120b] px-3 py-1 text-[10px] tracking-widest">FIRST DROP</span>
            </div>
            <div className="absolute bottom-8 left-8 right-8 text-[#f6efe1]">
              <p className="font-display text-5xl leading-none mb-3">Mango<br />Chill</p>
              <p className="text-sm opacity-80 mb-4">Ripe Alphonso mango. Cooling mint. Layered dark chocolate. Hand-broken, never uniform.</p>
              <div className="flex flex-wrap gap-2">
                <span className="text-[10px] tracking-widest uppercase border border-[#f6efe1]/40 px-2 py-1">Alphonso</span>
                <span className="text-[10px] tracking-widest uppercase border border-[#f6efe1]/40 px-2 py-1">Mint</span>
                <span className="text-[10px] tracking-widest uppercase border border-[#f6efe1]/40 px-2 py-1">Dark 62%</span>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#b78d3c]/15 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-[#e59a2b]/15 rounded-full blur-3xl"></div>
      </section>

      {/* Flavor grid */}
      <section className="section">
        <div className="container-lux">
          <div className="text-center mb-14">
            <p className="tracking-luxe text-[#b78d3c] mb-3">The Six</p>
            <h2 className="font-display text-5xl md:text-6xl">Meet the flavours</h2>
            <p className="text-[#3a2a1e]/70 mt-4 max-w-xl mx-auto">Each bark is composed, not manufactured. Every slab is different, every bite is intentional.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BARK_FLAVORS.map((b) => (
              <article key={b.id} className="group relative aspect-[3/4] overflow-hidden lift cursor-pointer" onClick={() => setSelectedFlavor(b.id)}>
                <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${b.color} 0%, #1a120b 90%)` }}></div>
                <div className="absolute inset-0 grain"></div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/25"></div>
                <div className="absolute top-5 left-5 right-5 flex justify-between">
                  <span className="tracking-luxe text-[#f6efe1]/85">Bark</span>
                  <span className={`text-[10px] tracking-widest px-2 py-1 ${b.badge === 'Signature' ? 'bg-[#f6efe1] text-[#1a120b]' : 'border border-[#f6efe1]/40 text-[#f6efe1]'}`}>{b.badge.toUpperCase()}</span>
                </div>
                <div className="absolute bottom-6 left-6 right-6 text-[#f6efe1]">
                  <h3 className="font-display text-3xl leading-tight mb-2">{b.name}</h3>
                  <p className="text-sm opacity-80">{b.notes}</p>
                  <a
                    href={waLink(b.name)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs tracking-widest uppercase border-b border-[#b78d3c] text-[#b78d3c] pb-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Notify me
                  </a>
                </div>
                {selectedFlavor === b.id && (
                  <div className="absolute top-5 right-5 mt-8 w-6 h-6 rounded-full bg-[#b78d3c] text-[#1a120b] flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Notify via WhatsApp */}
      <section className="section bg-[#efe6d3]">
        <div className="container-lux max-w-2xl text-center">
          <div className="w-14 h-14 rounded-full bg-[#1a120b] text-[#b78d3c] flex items-center justify-center mx-auto mb-6">
            <MessageCircle className="w-6 h-6" />
          </div>
          <p className="tracking-luxe text-[#b78d3c] mb-3">Early Access</p>
          <h2 className="font-display text-4xl md:text-5xl mb-4">Be the first to know.</h2>
          <p className="text-[#3a2a1e]/75 mb-8">Get 15% off the first drop, plus a heads-up 48 hours before public launch — message us on WhatsApp.</p>

          <div className="space-y-6">
            <div>
              <p className="tracking-luxe text-[#1a120b]/70 mb-3">Choose your first bark</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {BARK_FLAVORS.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setSelectedFlavor(b.id)}
                    className={`px-3 py-2.5 text-sm border transition-colors ${selectedFlavor === b.id ? 'bg-[#1a120b] text-[#f6efe1] border-[#1a120b]' : 'border-[#1a120b]/25 hover:border-[#1a120b]'}`}
                  >{b.name}</button>
                ))}
              </div>
            </div>
            <a
              href={waLink(BARK_FLAVORS.find((b) => b.id === selectedFlavor)?.name || '')}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp"
            >
              Notify me on WhatsApp <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* How it's made */}
      <section className="section">
        <div className="container-lux grid md:grid-cols-3 gap-10">
          {[
            { t: 'Composed, not made', d: 'Every slab is hand-poured and hand-broken. No two barks are ever identical.' },
            { t: 'Real inclusions', d: 'Whole fruit, roasted nuts, dried botanicals nothing artificial, nothing synthetic.' },
            { t: 'Small batch', d: 'Limited production runs. Once a batch is gone, the next may not be for months.' },
          ].map((x, i) => (
            <div key={i} className="border-t border-[#1a120b]/15 pt-6">
              <Leaf className="w-5 h-5 text-[#b78d3c] mb-4" />
              <h3 className="font-display text-2xl mb-3">{x.t}</h3>
              <p className="text-[#3a2a1e]/75 leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default BarksComingSoon;
